import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
  Loader2,
  LogOut,
  ArrowLeft,
  CheckCircle2,
  Circle,
  Mail,
  Phone,
  Search,
  AlertCircle,
  MessageCircle,
  StickyNote,
  Eye,
  Minus,
  Plus,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { hasAdminAccess } from "@/lib/admin-access";
import { AdminShell } from "@/components/admin/AdminShell";
import { useAdminSearch, matchesQuery } from "@/components/admin/AdminSearchContext";
import { SortControl, compareBySort, useAdminSort } from "@/components/admin/SortControl";
import { RescheduleBadge } from "@/components/admin/RescheduleBadge";
import { listRecentDateChanges, type DateChangeBadge } from "@/lib/student-lifecycle.functions";
import { updateInterviewStatus, type InterviewStatus } from "@/lib/admin-interviews.functions";
import { buildWhatsAppUrl, waDigits } from "@/lib/phone";
import { useIsTesterAccount } from "@/lib/tester";
import {
  listPaidStudents,
  updateStudentMilestone,
  getStudentOnboardingDetails,
  PLAN_MOCK_COUNT,
  effectiveMockTotal,
  type PaidStudent,
  type StudentOnboardingDetails,
} from "@/lib/admin-students.functions";



export const Route = createFileRoute("/admin/students")({
  head: () => ({
    meta: [
      { title: "Student progress - Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminStudentsPage,
});

function AdminStudentsPage() {
  const navigate = useNavigate();
  const { query } = useAdminSearch();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [students, setStudents] = useState<PaidStudent[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [planFilter, setPlanFilter] = useState<string>("All");
  const [sort, setSort] = useAdminSort("students");
  const [drawerId, setDrawerId] = useState<string | null>(null);
  const cardRefs = useRef<Record<string, HTMLElement | null>>({});


  const [changesByEmail, setChangesByEmail] = useState<Record<string, DateChangeBadge>>({});

  const listFn = useServerFn(listPaidStudents);
  const updateFn = useServerFn(updateStudentMilestone);
  const changesFn = useServerFn(listRecentDateChanges);
  const outcomeFn = useServerFn(updateInterviewStatus);

  const load = useCallback(async () => {
    try {
      const res = await listFn();
      setStudents(res.students);
      const emails = Array.from(
        new Set(res.students.map((s) => (s.email ?? "").trim().toLowerCase()).filter((e) => e.includes("@"))),
      ).slice(0, 500);
      if (emails.length) {
        try {
          const ch = await changesFn({ data: { emails } });
          setChangesByEmail(ch.byEmail ?? {});
        } catch {
          /* badges are best-effort */
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't load students");
    }
  }, [listFn, changesFn]);

  useEffect(() => {
    (async () => {
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        navigate({ to: "/auth", search: { redirect: "/admin/students" } });
        return;
      }
      if (!(await hasAdminAccess(sess.session))) {
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      await load();
      setLoading(false);
    })();
  }, [navigate, load]);

  const patch = async (s: PaidStudent, patchData: Partial<PaidStudent["milestones"]>) => {
    setSavingId(s.payment_id);
    setStudents((arr) =>
      arr.map((x) =>
        x.payment_id === s.payment_id
          ? { ...x, milestones: { ...x.milestones, ...patchData } }
          : x,
      ),
    );
    try {
      await updateFn({ data: { paymentId: s.payment_id, ...patchData } });
    } catch (e) {
      toast.error("Couldn't update");
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const setOutcome = async (s: PaidStudent, status: InterviewStatus) => {
    const prev = students;
    setStudents((arr) =>
      arr.map((x) => (x.payment_id === s.payment_id ? { ...x, interview_status: status } : x)),
    );
    setSavingId(s.payment_id);
    try {
      await outcomeFn({ data: { paymentId: s.payment_id, status } });
      toast.success("Outcome updated");
    } catch (e) {
      setStudents(prev);
      toast.error(e instanceof Error ? e.message : "Couldn't update outcome");
    } finally {
      setSavingId(null);
    }
  };

  const filtered = useMemo(() => {
    const list = students.filter((s) => {
      if (planFilter !== "All" && s.plan_name !== planFilter) return false;
      return matchesQuery(query, s.name, s.email, s.phone, s.plan_name);
    });
    return [...list].sort((a, b) =>
      compareBySort(
        { name: a.name, createdAt: a.created_at },
        { name: b.name, createdAt: b.created_at },
        sort,
      ),
    );
  }, [students, query, planFilter, sort]);


  const todayKey = new Date().toISOString().slice(0, 10);
  const upcoming = useMemo(
    () => filtered.filter((s) => !!s.interview_date && (s.interview_date as string) >= todayKey),
    [filtered, todayKey],
  );
  const past = useMemo(
    () => filtered.filter((s) => !s.interview_date || (s.interview_date as string) < todayKey),
    [filtered, todayKey],
  );

  // Historical approval metrics - recomputed from live state on every outcome change.
  const metrics = useMemo(() => {
    const decided = students.filter((s) => s.interview_status && s.interview_status !== "pending");
    const approved = decided.filter((s) => s.interview_status === "approved").length;
    const refused = decided.filter((s) => s.interview_status === "refused").length;
    const processing = decided.filter((s) => s.interview_status === "admin_processing").length;
    const rate = decided.length ? Math.round((approved / decided.length) * 100) : 0;
    return { total: students.length, decided: decided.length, approved, refused, processing, rate };
  }, [students]);

  // Pending approval queue: any student with mocks_requested > mocks_done.
  // (DS-160 review requests are handled inline on the student card, not surfaced here.)
  const pending = useMemo(() => {
    const items: { student: PaidStudent; label: string }[] = [];
    for (const s of students) {
      const m = s.milestones;
      if (m.mocks_requested > m.mocks_done) {
        for (let n = m.mocks_done + 1; n <= m.mocks_requested; n++) {
          items.push({ student: s, label: `Mock ${n} · Pending Approval` });
        }
      }
    }
    return items;
  }, [students]);

  const scrollTo = (paymentId: string) => {
    const el = cardRefs.current[paymentId];
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-amber-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 1800);
    }
  };

  if (loading)
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  if (!authorized)
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl">Not authorized</h1>
        </div>
      </div>
    );

  return (
    <AdminShell
      title="Student progress"
      description={`${students.length} paid student${students.length === 1 ? "" : "s"} · toggle each milestone as they complete it.`}
      actions={
        <Button
          variant="outline"
          onClick={async () => {
            await supabase.auth.signOut();
            navigate({ to: "/auth" });
          }}
          className="gap-2"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      }
    >

      {/* Pending Approvals Queue */}
      <div
        className={
          "mt-6 rounded-2xl border p-5 " +
          (pending.length > 0
            ? "border-amber-300 bg-amber-50"
            : "border-dashed border-border bg-muted/30")
        }
      >
        <div className="flex items-center gap-2">
          <AlertCircle
            className={"h-4 w-4 " + (pending.length > 0 ? "text-amber-600" : "text-muted-foreground")}
          />
          <h2 className="font-display text-base font-semibold">
            Pending Approvals Queue{" "}
            <span className="ml-1 text-sm font-normal text-muted-foreground">
              ({pending.length})
            </span>
          </h2>
        </div>
        {pending.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            All caught up - no mock or DS-160 requests awaiting approval.
          </p>
        ) : (
          <ul className="mt-3 flex flex-wrap gap-2">
            {pending.map((item, i) => (
              <li key={`${item.student.payment_id}-${i}`}>
                <button
                  type="button"
                  onClick={() => scrollTo(item.student.payment_id)}
                  className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100"
                >
                  <span className="font-semibold">{item.student.name}</span>
                  <span className="text-amber-700">· {item.label}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <SortControl value={sort} onChange={setSort} label="Sort students" className="mr-1" />
        {["All", "Basic Support", "Full Support", "Refusal Support"].map((p) => {
          const active = planFilter === p;
          const count =
            p === "All" ? students.length : students.filter((s) => s.plan_name === p).length;
          return (
            <button
              key={p}
              type="button"
              onClick={() => setPlanFilter(p)}
              className={
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition " +
                (active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-background text-foreground hover:bg-muted")
              }
            >
              {p}
              <span
                className={
                  "rounded-full px-1.5 py-0.5 text-[10px] " +
                  (active ? "bg-primary-foreground/20" : "bg-muted text-muted-foreground")
                }
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {query && (
        <p className="mt-3 text-xs text-muted-foreground">
          Filtering by top-bar search: <span className="font-medium text-foreground">"{query}"</span>
        </p>
      )}


      {/* Live approval metrics */}
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { label: "Paid students", value: metrics.total },
          { label: "Outcomes recorded", value: metrics.decided },
          { label: "Approved", value: metrics.approved },
          { label: "Refused / 221(g)", value: metrics.refused + metrics.processing },
          { label: "Approval rate", value: `${metrics.rate}%` },
        ].map((m) => (
          <div key={m.label} className="rounded-xl border border-border bg-card px-3 py-2.5">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{m.label}</p>
            <p className="mt-0.5 font-display text-xl font-semibold tabular-nums">{m.value}</p>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="mt-6 rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          No students match your search.
        </div>
      )}

      {[
        { key: "upcoming", title: "Upcoming Interviews", rows: upcoming },
        { key: "past", title: "Past Interviews", rows: past },
      ].map((section) =>
        section.rows.length === 0 ? null : (
          <section key={section.key} className="mt-8">
            <h2 className="font-display text-base font-semibold">
              {section.title}{" "}
              <span className="ml-1 text-sm font-normal text-muted-foreground">({section.rows.length})</span>
            </h2>
            <div className="mt-3 grid gap-3">
              {section.rows.map((s) => (
                <StudentCard
                  key={s.payment_id}
                  student={s}
                  saving={savingId === s.payment_id}
                  onPatch={patch}
                  onOutcome={setOutcome}
                  change={changesByEmail[(s.email ?? "").trim().toLowerCase()]}
                  onPreview={() => setDrawerId(s.payment_id)}
                  cardRef={(el) => {
                    cardRefs.current[s.payment_id] = el;
                  }}
                />
              ))}
            </div>
          </section>
        ),
      )}

      <StudentDrawer
        student={students.find((s) => s.payment_id === drawerId) ?? null}
        onClose={() => setDrawerId(null)}
        onPatch={patch}
        saving={savingId !== null}
      />

    </AdminShell>
  );
}

function StudentCard({
  student: s,
  saving,
  onPatch,
  onOutcome,
  change,
  onPreview,
  cardRef,
}: {
  student: PaidStudent;
  saving: boolean;
  onPatch: (s: PaidStudent, p: Partial<PaidStudent["milestones"]>) => Promise<void>;
  onOutcome: (s: PaidStudent, status: InterviewStatus) => Promise<void>;
  change?: DateChangeBadge;
  onPreview: () => void;
  cardRef: (el: HTMLElement | null) => void;
}) {
  const totalMocks = effectiveMockTotal(s.plan_name, s.milestones.mock_target_override);
  const done = s.milestones;
  const totalSteps = 1 + totalMocks + 1; // DS-160 + mocks + ready
  const todayStr = new Date().toISOString().slice(0, 10);
  const interviewCompleted = !!(s.interview_date && s.interview_date < todayStr);
  const completedSteps = interviewCompleted
    ? totalSteps
    : (done.ds160_review_done ? 1 : 0) +
      Math.min(done.mocks_done, totalMocks) +
      (done.ready_for_interview ? 1 : 0);
  const pct = Math.round((completedSteps / totalSteps) * 100);


  const [notes, setNotes] = useState(done.admin_notes ?? "");
  const [notesSaving, setNotesSaving] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  useEffect(() => {
    setNotes(done.admin_notes ?? "");
  }, [done.admin_notes]);

  const phoneDigits = waDigits(s.phone);
  const interviewLine = s.interview_date ? `interview date ${s.interview_date}` : "interview date _______";
  const waMsg = `Hi ${s.name.split(" ")[0] || "there"}! This is the WeLivdIt admin team - checking in on your ${s.plan_name} (${interviewLine}). Let us know how we can help next.`;
  const waHref = buildWhatsAppUrl(s.phone, waMsg);

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await onPatch(s, { admin_notes: notes });
      toast.success("Notes saved");
    } finally {
      setNotesSaving(false);
    }
  };

  return (
    <article
      ref={cardRef}
      className="rounded-2xl border border-border bg-card p-5 transition-all duration-300"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-lg font-semibold">{s.name}</h3>
            <Badge variant="secondary">{s.plan_name}</Badge>
            <RescheduleBadge change={change} />
            {!s.user_id && (
              <Badge variant="outline" className="text-xs">
                not signed up yet
              </Badge>
            )}
            {done.mocks_requested > done.mocks_done && (
              <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                Mock {done.mocks_done + 1} · Pending Approval
              </Badge>
            )}
            {done.ds160_review_requested && !done.ds160_review_done && (
              <Badge className="bg-amber-500 text-white hover:bg-amber-500">
                DS-160 · Pending Approval
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {s.email && (
              <a
                href={`mailto:${s.email}`}
                className="inline-flex items-center gap-1 hover:text-primary"
              >
                <Mail className="h-3.5 w-3.5" /> {s.email}
              </a>
            )}
            {s.phone && (
              <span className="inline-flex items-center gap-2">
                <span className="inline-flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" /> {s.phone}
                </span>
                {phoneDigits && (
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    <MessageCircle className="h-3 w-3" /> WhatsApp
                  </a>
                )}
              </span>
            )}
            {s.interview_date && (
              <span className="text-xs">
                Interview: {s.interview_date}
                {interviewCompleted && (
                  <span className="ml-2 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                    Completed
                  </span>
                )}
              </span>
            )}
          </div>
          <div className="mt-2 text-xs text-muted-foreground">
            {completedSteps} of {totalSteps} milestones · {pct}%
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <select
            aria-label={`Visa outcome for ${s.name}`}
            value={s.interview_status ?? "pending"}
            disabled={saving}
            onChange={(e) => void onOutcome(s, e.target.value as InterviewStatus)}
            className="h-8 rounded-lg border border-border bg-background px-2 text-xs"
          >
            <option value="pending">Outcome: Pending</option>
            <option value="approved">Approved</option>
            <option value="refused">Refused</option>
            <option value="admin_processing">221(g) Processing</option>
          </select>
          <Button variant="outline" size="sm" onClick={onPreview} className="gap-1.5">
            <Eye className="h-3.5 w-3.5" /> Quick preview
          </Button>
        </div>
      </div>


      <div className="mt-4 flex flex-wrap gap-2">
        <MilestoneButton
          label="DS-160 Review Done"
          checked={done.ds160_review_done}
          pending={done.ds160_review_requested && !done.ds160_review_done}
          disabled={saving}
          onClick={() =>
            onPatch(s, {
              ds160_review_done: !done.ds160_review_done,
              // when admin marks done, clear the request flag
              ds160_review_requested: done.ds160_review_done ? done.ds160_review_requested : false,
            })
          }
        />
        {Array.from({ length: totalMocks }, (_, i) => i + 1).map((n) => {
          const checked = done.mocks_done >= n;
          const pendingThis = done.mocks_requested >= n && done.mocks_done < n;
          return (
            <MilestoneButton
              key={n}
              label={`Mock ${n}`}
              checked={checked}
              pending={pendingThis}
              disabled={saving}
              onClick={() => onPatch(s, { mocks_done: checked ? n - 1 : n })}
            />
          );
        })}
        <MilestoneButton
          label="Ready for Interview"
          checked={done.ready_for_interview}
          disabled={saving}
          onClick={() => onPatch(s, { ready_for_interview: !done.ready_for_interview })}
        />
      </div>

      {/* Dev-only debug panel: confirms derived totals so we can see 100% */}
      {import.meta.env.DEV && (
        <div className="mt-3 rounded-lg border border-dashed border-border bg-muted/40 px-3 py-2 font-mono text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">debug:</span>{" "}
          plan.mocks={totalMocks} · mocks_done={done.mocks_done} · mocks_requested=
          {done.mocks_requested} · ds160_done={String(done.ds160_review_done)} · ready=
          {String(done.ready_for_interview)} · steps={completedSteps}/{totalSteps} · pct={pct}%
          {pct === 100 && <span className="ml-1 text-emerald-600">✓ complete</span>}
        </div>
      )}

      {/* Internal notes */}
      <div className="mt-4 border-t border-border pt-3">
        <button
          type="button"
          onClick={() => setShowNotes((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"
        >
          <StickyNote className="h-3.5 w-3.5" />
          {showNotes ? "Hide" : "Show"} internal notes
          {!showNotes && notes && <span className="text-amber-700">· has notes</span>}
        </button>
        {showNotes && (
          <div className="mt-2 space-y-2">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Private notes (hidden from student): mock feedback, follow-ups, weak spots…"
              className="min-h-[90px] text-sm"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={saveNotes} disabled={notesSaving}>
                {notesSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save notes"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function MilestoneButton({
  label,
  checked,
  pending,
  disabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  pending?: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  const cls = checked
    ? "border-emerald-300 bg-emerald-50 text-emerald-900"
    : pending
      ? "border-amber-300 bg-amber-50 text-amber-900"
      : "border-border bg-background hover:bg-muted";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition " + cls
      }
    >
      {checked ? (
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
      ) : pending ? (
        <AlertCircle className="h-4 w-4 text-amber-600" />
      ) : (
        <Circle className="h-4 w-4 text-muted-foreground" />
      )}
      {label}
      {pending && !checked && <span className="text-xs">· approve?</span>}
    </button>
  );
}

function StudentDrawer({
  student,
  onClose,
  onPatch,
  saving,
}: {
  student: PaidStudent | null;
  onClose: () => void;
  onPatch: (s: PaidStudent, p: Partial<PaidStudent["milestones"]>) => Promise<void>;
  saving: boolean;
}) {
  const open = !!student;
  const isTester = useIsTesterAccount();
  const [notes, setNotes] = useState("");
  const [notesSaving, setNotesSaving] = useState(false);

  useEffect(() => {
    setNotes(student?.milestones.admin_notes ?? "");
  }, [student?.payment_id, student?.milestones.admin_notes]);

  if (!student) {
    return (
      <Sheet open={false} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent />
      </Sheet>
    );
  }

  const m = student.milestones;
  const planDefault = PLAN_MOCK_COUNT[student.plan_name] ?? 2;
  const target = m.mock_target_override ?? planDefault;

  const setTarget = (n: number) => {
    const clamped = Math.max(1, Math.min(6, n));
    const value = clamped === planDefault ? null : clamped;
    onPatch(student, { mock_target_override: value });
  };

  const saveNotes = async () => {
    setNotesSaving(true);
    try {
      await onPatch(student, { admin_notes: notes });
      toast.success("Notes saved");
    } finally {
      setNotesSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <SheetTitle className="font-display text-2xl">{student.name}</SheetTitle>
            <Badge variant="secondary">{student.plan_name}</Badge>
            <span
              className={
                "ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition-opacity " +
                (saving
                  ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-200"
                  : "border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-950/30 dark:text-emerald-200")
              }
              aria-live="polite"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3 w-3 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3 w-3" /> Saved
                </>
              )}
            </span>
          </div>
          <SheetDescription className="space-y-0.5">
            {student.email && <div>{student.email}</div>}
            {student.phone && <div>{student.phone}</div>}
            {student.interview_date && <div>Interview: {student.interview_date}</div>}
          </SheetDescription>
        </SheetHeader>

        {isTester && <StudentOnboardingPanel email={student.email} userId={student.user_id ?? null} />}

        <div className="mt-6 space-y-6 text-sm">

          {/* Mock target */}
          <div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Mock target</p>
                <p className="text-[11px] text-muted-foreground">
                  Plan default: {planDefault} · Currently: {m.mocks_done}/{target} done
                </p>
              </div>
              <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1">
                <button
                  type="button"
                  disabled={saving || target <= 1}
                  onClick={() => setTarget(target - 1)}
                  className="grid h-7 w-7 place-items-center rounded-full hover:bg-muted disabled:opacity-40"
                  aria-label="Decrease mock target"
                >
                  <Minus className="h-3.5 w-3.5" />
                </button>
                <span className="min-w-[1.5rem] text-center font-mono text-sm font-semibold">{target}</span>
                <button
                  type="button"
                  disabled={saving || target >= 6}
                  onClick={() => setTarget(target + 1)}
                  className="grid h-7 w-7 place-items-center rounded-full hover:bg-muted disabled:opacity-40"
                  aria-label="Increase mock target"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>

          {/* Status toggles */}
          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">Status</p>
            <div className="flex flex-col gap-2">
              <StatusToggle
                label="DS-160 Reviewed"
                checked={m.ds160_review_done}
                disabled={saving}
                onClick={() => onPatch(student, {
                  ds160_review_done: !m.ds160_review_done,
                  ds160_review_requested: m.ds160_review_done ? m.ds160_review_requested : false,
                })}
              />
              <StatusToggle
                label="SEVIS Paid"
                checked={m.sevis_paid}
                disabled={saving}
                onClick={() => onPatch(student, { sevis_paid: !m.sevis_paid })}
              />
              <StatusToggle
                label="Ready for Interview"
                checked={m.ready_for_interview}
                disabled={saving}
                onClick={() => onPatch(student, { ready_for_interview: !m.ready_for_interview })}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <p className="mb-2 text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1.5">
              <StickyNote className="h-3.5 w-3.5" /> Internal coaching notes
            </p>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Private notes - mock feedback, weak spots, next-step reminders…"
              className="min-h-[120px] text-sm"
            />
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={saveNotes} disabled={notesSaving}>
                {notesSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save notes"}
              </Button>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function StatusToggle({
  label,
  checked,
  disabled,
  onClick,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        "flex items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm transition " +
        (checked
          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
          : "border-border bg-background hover:bg-muted")
      }
    >
      <span className="inline-flex items-center gap-2">
        {checked ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <Circle className="h-4 w-4 text-muted-foreground" />
        )}
        {label}
      </span>
      <span className="text-xs text-muted-foreground">{checked ? "On" : "Off"}</span>
    </button>
  );
}


/** Onboarding answers + payment receipt preview for the student drawer. */
function StudentOnboardingPanel({ email, userId }: { email: string | null; userId: string | null }) {
  const fetchDetails = useServerFn(getStudentOnboardingDetails);
  const [details, setDetails] = useState<StudentOnboardingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const r = await fetchDetails({ data: { email, userId } });
        if (!cancelled) setDetails(r.details);
      } catch {
        if (!cancelled) setDetails(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchDetails, email, userId]);

  const rows: Array<[string, string | null]> = [
    ["Target consulate", details?.target_consulate ?? null],
    ["University", details?.university ?? null],
    ["Major / degree", details?.target_degree ?? null],
    ["Intake term", details?.intake_term ?? null],
    ["Booked visa slots", details?.booked_visa_slots ?? null],
    ["Biometrics", details?.biometrics_date ?? null],
    ["Interview date", details?.visa_date ?? null],
    ["Visa history", details?.visa_history ?? null],
  ];

  const proof = details?.payment_proof_url ?? null;
  const isPdf = Boolean(proof && proof.split("?")[0]?.toLowerCase().endsWith(".pdf"));

  return (
    <div className="mt-5 rounded-xl border border-[#E2E8F0] bg-[#FAF8F5] p-4">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Academic profile</p>
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading onboarding details…
        </div>
      ) : (
        <dl className="mt-3 grid gap-x-4 gap-y-2 sm:grid-cols-2">
          {rows.map(([label, value]) => (
            <div key={label} className="text-xs">
              <dt className="text-muted-foreground">{label}</dt>
              <dd className="font-medium text-foreground">{value || "—"}</dd>
            </div>
          ))}
        </dl>
      )}

      <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-slate-500">Payment receipt</p>
      {proof ? (
        <div className="mt-2 space-y-2">
          {isPdf ? (
            <a
              href={proof}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700"
            >
              <Eye className="h-3.5 w-3.5" /> View Full Receipt (PDF)
            </a>
          ) : (
            <button type="button" onClick={() => setLightbox(true)} className="block">
              <img
                src={proof}
                alt="Payment receipt thumbnail"
                loading="lazy"
                className="h-28 w-auto rounded-lg border border-[#E2E8F0] object-cover hover:opacity-90"
              />
              <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-primary">
                <Eye className="h-3 w-3" /> View Full Receipt
              </span>
            </button>
          )}
        </div>
      ) : (
        <span className="mt-2 inline-flex rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-[11px] font-semibold text-rose-700">
          No Receipt Uploaded
        </span>
      )}

      {lightbox && proof && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-6"
          onClick={() => setLightbox(false)}
          role="presentation"
        >
          <img src={proof} alt="Payment receipt" className="max-h-[85vh] max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
